import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { AccountingAuditTimelineComponent } from '../../shared/accounting-audit-timeline.component';
import { AccountingAttachmentsPanelComponent } from '../../shared/accounting-attachments-panel.component';
import { AccountingGlPreviewPanelComponent } from '../../shared/accounting-gl-preview-panel.component';
import { AccountingTabLabelComponent } from '../../shared/accounting-tab-label.component';
import { AccountingWorkflowActionsComponent } from '../../shared/accounting-workflow-actions.component';
import { ManualSourceBadgeComponent } from '../../shared/manual-source-badge.component';
import { AccountingDocumentAction, DocumentAuditEvent, GlPreview } from '../../accounting-lifecycle.model';
import { SessionStorageService } from '../../../services/session-storage.service';
import { JournalEntry } from '../models/journal-entry-header.model';
import { JournalItem } from '../models/journal-entry-detail.model';
import { JournalEntriesService } from '../journal-entries.service';

@Component({
    selector: 'app-journal-entry-detail',
    standalone: true,
    imports: [
        CommonModule,
        ButtonModule,
        TableModule,
        TabsModule,
        TagModule,
        ToastModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageDataPanelComponent,
        AccountingTabLabelComponent,
        AccountingWorkflowActionsComponent,
        AccountingAuditTimelineComponent,
        AccountingGlPreviewPanelComponent,
        AccountingAttachmentsPanelComponent,
        ManualSourceBadgeComponent
    ],
    providers: [JournalEntriesService, MessageService, SessionStorageService],
    template: `
        <app-page-shell>
            <p-toast position="top-center"></p-toast>

            <app-page-detail-hero *ngIf="entry" eyebrow="Journal Entry" [title]="entry.entryNumber || ('JE-' + entry.entryId)"
                [subtitle]="entry.reference || $any(entry).journal?.journalName || 'Accounting journal entry'" icon="pi pi-book">
                <div tags>
                    <p-tag [value]="entry.documentStatus || entry.state || 'DRAFT'" [severity]="statusSeverity(entry.documentStatus || entry.state)"></p-tag>
                    <p-tag [value]="entry.workflowStatus || 'WORKFLOW_PENDING'" severity="info"></p-tag>
                    <p-tag [value]="entry.postingStatus || entry.state || 'UNPOSTED'" [severity]="statusSeverity(entry.postingStatus || entry.state)"></p-tag>
                    <p-tag [value]="money(entry.amount, entry.currencyCode)" severity="secondary"></p-tag>
                    <p-tag *ngIf="isImbalanced()" value="IMBALANCED" severity="danger"></p-tag>
                </div>
                <div actions>
                    <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (onClick)="back()"></p-button>
                    <p-button label="Edit" icon="pi pi-pencil" severity="secondary" [outlined]="true" *ngIf="canEdit()" (onClick)="edit()"></p-button>
                    <app-accounting-workflow-actions [documentStatus]="entry.documentStatus" [postingStatus]="entry.postingStatus"
                        (workflow)="workflow($event.action, $event.reason)" (post)="post()"></app-accounting-workflow-actions>
                </div>
            </app-page-detail-hero>

            <p-tabs [value]="activeTab" (valueChange)="onTabChange($event)" *ngIf="entry">
                <p-tablist>
                    <p-tab value="overview"><app-accounting-tab-label icon="pi pi-info-circle" label="Overview"></app-accounting-tab-label></p-tab>
                    <p-tab value="lines"><app-accounting-tab-label icon="pi pi-list" label="Lines"></app-accounting-tab-label></p-tab>
                    <p-tab value="journal"><app-accounting-tab-label icon="pi pi-book" label="GL Preview"></app-accounting-tab-label></p-tab>
                    <p-tab value="workflow"><app-accounting-tab-label icon="pi pi-sitemap" label="Workflow"></app-accounting-tab-label></p-tab>
                    <p-tab value="attachments"><app-accounting-tab-label icon="pi pi-paperclip" label="Attachments"></app-accounting-tab-label></p-tab>
                    <p-tab value="activity"><app-accounting-tab-label icon="pi pi-history" label="Activity"></app-accounting-tab-label></p-tab>
                    <p-tab value="related"><app-accounting-tab-label icon="pi pi-share-alt" label="Related"></app-accounting-tab-label></p-tab>
                </p-tablist>
                <p-tabpanels>
                    <p-tabpanel value="overview">
                        <app-page-data-panel title="Entry Summary" [resultText]="(entry.items || []).length + ' lines'">
                            <div class="detail-grid">
                                <div><strong>Journal</strong><span>{{ $any(entry).journal?.journalName || '-' }}</span></div>
                                <div><strong>Date</strong><span>{{ entry.entryDate | date:'yyyy-MM-dd' }}</span></div>
                                <div><strong>Party</strong><span>{{ $any(entry).party?.entityName || '-' }}</span></div>
                                <div><strong>Reference</strong><span>{{ entry.reference || '-' }}</span></div>
                                <div><strong>Total debit</strong><span>{{ money(totalDebit(), entry.currencyCode) }}</span></div>
                                <div><strong>Total credit</strong><span>{{ money(totalCredit(), entry.currencyCode) }}</span></div>
                                <div><strong>Imbalance</strong><span [class.danger]="isImbalanced()">{{ money(totalDebit() - totalCredit(), entry.currencyCode) }}</span></div>
                                <div><strong>Currency</strong><span>{{ entry.currencyCode || '-' }}</span></div>
                            </div>
                        </app-page-data-panel>
                    </p-tabpanel>

                    <p-tabpanel value="lines">
                        <app-page-data-panel title="Journal Lines" [resultText]="(entry.items || []).length + ' lines'">
                            <p-table [value]="entry.items || []" responsiveLayout="scroll">
                                <ng-template pTemplate="header">
                                    <tr>
                                        <th>Account</th>
                                        <th>Party</th>
                                        <th>Description</th>
                                        <th>Dimensions</th>
                                        <th class="numeric">Debit</th>
                                        <th class="numeric">Credit</th>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-line>
                                    <tr>
                                        <td>{{ accountLabel(line) }}</td>
                                        <td>{{ line.party?.entityName || line.partnerName || '-' }}</td>
                                        <td>{{ line.name || '-' }}</td>
                                        <td>
                                            <span *ngIf="!line.dimensions?.length">-</span>
                                            <span class="dimension-chip" *ngFor="let dim of line.dimensions || []">
                                                {{ dim.dimensionType?.name }}: {{ dim.dimensionValueName }}
                                            </span>
                                        </td>
                                        <td class="numeric">{{ money(line.debit, entry.currencyCode) }}</td>
                                        <td class="numeric">{{ money(line.credit, entry.currencyCode) }}</td>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="footer">
                                    <tr>
                                        <td colspan="4" class="numeric"><strong>Total</strong></td>
                                        <td class="numeric"><strong>{{ money(totalDebit(), entry.currencyCode) }}</strong></td>
                                        <td class="numeric"><strong>{{ money(totalCredit(), entry.currencyCode) }}</strong></td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        </app-page-data-panel>
                    </p-tabpanel>

                    <p-tabpanel value="journal">
                        <app-accounting-gl-preview-panel [preview]="glPreview"></app-accounting-gl-preview-panel>
                    </p-tabpanel>

                    <p-tabpanel value="workflow">
                        <app-page-data-panel title="Workflow State">
                            <div class="detail-grid">
                                <div><strong>Document</strong><span>{{ entry.documentStatus || '-' }}</span></div>
                                <div><strong>Workflow</strong><span>{{ entry.workflowStatus || '-' }}</span></div>
                                <div><strong>Posting</strong><span>{{ entry.postingStatus || '-' }}</span></div>
                                <div><strong>Submitted</strong><span>{{ entry.submittedAt | date:'short' }}</span></div>
                                <div><strong>Approved</strong><span>{{ entry.approvedAt | date:'short' }}</span></div>
                                <div><strong>Posted</strong><span>{{ entry.postedAt | date:'short' }}</span></div>
                                <div><strong>Reversal Entry</strong><span>{{ entry.reversalJournalEntryId || '-' }}</span></div>
                                <div><strong>Reversal Reason</strong><span>{{ entry.reversalReason || '-' }}</span></div>
                            </div>
                        </app-page-data-panel>
                    </p-tabpanel>

                    <p-tabpanel value="attachments">
                        <app-accounting-attachments-panel documentType="JOURNAL_ENTRY" [documentId]="entry.entryId"
                            [owningCompanyId]="entry.owningCompanyId"></app-accounting-attachments-panel>
                    </p-tabpanel>

                    <p-tabpanel value="activity">
                        <app-accounting-audit-timeline [events]="auditEvents"></app-accounting-audit-timeline>
                    </p-tabpanel>

                    <p-tabpanel value="related">
                        <app-page-data-panel title="Source and Links">
                            <div class="detail-grid">
                                <div>
                                    <strong>Source</strong>
                                    <span>
                                        <app-manual-source-badge [sourceType]="$any(entry).sourceType || 'SYSTEM'"
                                            [sourceReference]="$any(entry).sourceReference"
                                            [manualReasonCode]="$any(entry).manualReasonCode"></app-manual-source-badge>
                                    </span>
                                </div>
                                <div><strong>Created From</strong><span>{{ $any(entry).createdFromModule || '-' }}</span></div>
                                <div><strong>Original Journal</strong><span>{{ entry.journalEntryId || '-' }}</span></div>
                                <div><strong>Reversal Journal</strong><span>{{ entry.reversalJournalEntryId || '-' }}</span></div>
                            </div>
                        </app-page-data-panel>
                    </p-tabpanel>
                </p-tabpanels>
            </p-tabs>
        </app-page-shell>
    `,
    styles: [`
        .detail-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
            gap: .75rem;
        }

        .detail-grid div {
            display: flex;
            flex-direction: column;
            gap: .25rem;
            min-width: 0;
            padding: .75rem;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            background: #ffffff;
        }

        .detail-grid span {
            color: #475569;
            overflow-wrap: anywhere;
        }

        .danger {
            color: #b91c1c !important;
            font-weight: 700;
        }

        .numeric {
            text-align: right;
            white-space: nowrap;
        }

        .dimension-chip {
            display: inline-flex;
            margin: .15rem .25rem .15rem 0;
            padding: .2rem .45rem;
            border: 1px solid #cbd5e1;
            border-radius: 6px;
            color: #475569;
            background: #f8fafc;
            font-size: .82rem;
            font-weight: 600;
        }
    `]
})
export class JournalEntryDetailComponent implements OnInit {
    entry?: JournalEntry;
    auditEvents: DocumentAuditEvent[] = [];
    glPreview?: GlPreview;
    activeTab = 'overview';

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private journalEntriesService: JournalEntriesService,
        private messageService: MessageService,
        private sessionStorageService: SessionStorageService
    ) {}

    ngOnInit(): void {
        this.route.queryParamMap.subscribe(queryParams => {
            this.activeTab = this.resolveTab(queryParams.get('tab'));
        });
        this.load();
    }

    onTabChange(value: string | number) {
        this.activeTab = this.resolveTab(String(value));
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.activeTab },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private resolveTab(tabKey: string | null): string {
        const tabs = ['overview', 'lines', 'journal', 'workflow', 'attachments', 'activity', 'related'];
        const normalized = (tabKey || 'overview').toLowerCase();
        return tabs.includes(normalized) ? normalized : 'overview';
    }

    load(): void {
        const id = Number(this.route.snapshot.paramMap.get('id'));
        if (!id) {
            return;
        }
        this.journalEntriesService.getJournalEntryById(id).subscribe({
            next: entry => {
                this.entry = entry;
                this.loadSupportingData(entry);
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Load failed', detail: err?.error?.message || 'Could not load journal entry.' })
        });
    }

    workflow(action: AccountingDocumentAction, reason?: string): void {
        this.runWorkflow(action, reason);
    }

    post(): void {
        this.runWorkflow('post', 'Posted from journal entry detail');
    }

    canEdit(): boolean {
        const documentStatus = (this.entry?.documentStatus || '').toUpperCase();
        const postingStatus = (this.entry?.postingStatus || this.entry?.state || '').toUpperCase();
        return !!this.entry?.entryId
            && postingStatus !== 'POSTED'
            && !['POSTED', 'CANCELLED', 'VOIDED', 'REVERSED', 'CLOSED'].includes(documentStatus);
    }

    edit(): void {
        if (this.entry?.entryId && this.canEdit()) {
            this.router.navigate(['/accounts/enterprise/journal-entries/edit', this.entry.entryId]);
        }
    }

    back(): void {
        this.location.back();
    }

    totalDebit(): number {
        return (this.entry?.items || []).reduce((sum, line) => sum + Number(line.debit || 0), 0);
    }

    totalCredit(): number {
        return (this.entry?.items || []).reduce((sum, line) => sum + Number(line.credit || 0), 0);
    }

    isImbalanced(): boolean {
        return Math.abs(this.totalDebit() - this.totalCredit()) > 0.0001;
    }

    accountLabel(line: JournalItem): string {
        if (!line.account) {
            return '-';
        }
        return `${line.account.accountCode || ''} ${line.account.accountName || ''}`.trim();
    }

    money(amount?: number, currencyCode = ''): string {
        return `${currencyCode || ''} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
    }

    statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const normalized = (status || '').toUpperCase();
        if (normalized === 'POSTED' || normalized === 'APPROVED') return 'success';
        if (normalized === 'DRAFT' || normalized === 'SUBMITTED') return 'info';
        if (normalized === 'ON_HOLD' || normalized === 'PENDING') return 'warn';
        if (['REJECTED', 'CANCELLED', 'VOIDED', 'REVERSED'].includes(normalized)) return 'danger';
        return 'secondary';
    }

    private loadSupportingData(entry: JournalEntry): void {
        const companyId = entry.owningCompanyId || this.sessionStorageService.getCompany()?.id || 0;
        const entryId = entry.entryId || 0;
        if (!companyId || !entryId) {
            return;
        }
        this.journalEntriesService.getJournalEntryAuditEvents(companyId, entryId).subscribe({
            next: events => this.auditEvents = events,
            error: () => this.auditEvents = []
        });
        this.journalEntriesService.getJournalEntryGlPreview(companyId, entryId).subscribe({
            next: preview => this.glPreview = preview,
            error: () => this.glPreview = undefined
        });
    }

    private runWorkflow(action: AccountingDocumentAction, reason?: string): void {
        if (!this.entry?.entryId) {
            return;
        }
        this.journalEntriesService.runJournalEntryWorkflow(
            this.entry.owningCompanyId || this.sessionStorageService.getCompany()?.id || 0,
            this.entry.entryId,
            action,
            { source: 'PORTAL', reason, actorId: this.actorId() }
        ).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Updated', detail: `Journal entry ${action} completed.` });
                this.load();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Action failed', detail: err?.error?.message || `Could not ${action} journal entry.` })
        });
    }

    private actorId(): number | undefined {
        const storage = this.sessionStorageService as any;
        const user = storage.getUser?.();
        return user?.id ?? user?.userId ?? storage.getCustomer?.()?.id;
    }
}
